import "./App.css";
import Card from "react-bootstrap/Card";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import CardFooter from "react-bootstrap/CardFooter";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import ListGroup from "react-bootstrap/ListGroup";
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useParams } from "react-router";
import * as html2pdf from "html2pdf.js";
import SanitizedHtml from "./components/SanitizedHtml";
import {
  applyValidityClass,
  updateGroupFeedback,
} from "./validation/validityStyling";
import {
  getOpenFormStorageKey,
  getAnswersStorageKey,
  readStorage,
  writeStorage,
  isPageRefresh,
} from "./persistence/sessionStorageKeys";
import {
  serializeFormValues,
  restoreFormValues,
} from "./persistence/formSerialization";
import buildControl from "./forms/buildControl";
import collectQuestionValue from "./forms/collectQuestionValue";
import hasRenderableControl from "./forms/hasRenderableControl";
import { syncDateValue } from "./forms/questions/DateQuestion";

function App() {
  let params = useParams();
  const [forms, setForms] = useState();
  const [branchName, setBranchName] = useState();
  const [databaseId, setDatabaseId] = useState();
  const [practiceMessage, setPracticeMessage] = useState();
  const [branchAddress, setBranchAddress] = useState();
  const [branchSuburb, setBranchSuburb] = useState();
  const [branchPostcode, setBranchPostcode] = useState();
  const [branchEmail, setBranchEmail] = useState();
  const [branchPhone, setBranchPhone] = useState();
  const [branchLogo, setBranchLogo] = useState();
  const [showForm, setShowForm] = useState(false);
  const [questions, setQuestions] = useState();
  const [selectedForm, setSelectedForm] = useState();
  const completedForm = useRef(null);
  const formRef = useRef(null);

  // Survives a browser refresh: which form is open, and the values already
  // typed into it, both scoped per form_id route so different practice
  // links in the same browser don't clobber each other's in-progress state.
  const openFormStorageKey = getOpenFormStorageKey(params.form_id);
  const answersStorageKey = (formId) =>
    getAnswersStorageKey(params.form_id, formId);

  const persistFormValues = () => {
    const form = formRef.current;
    if (!form || !selectedForm) return;
    writeStorage(
      answersStorageKey(selectedForm),
      JSON.stringify(serializeFormValues(form)),
    );
  };

  const loadPatientForms = () => {
    const postData = { formId: params.form_id };

    fetch("/api/v1/getBranchByFormId", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(postData),
    })
      .then((response) => response.json())
      .then((json) => {
        if (json.error) {
          console.log(
            "[ERROR] An error occured while loading form data during initialization",
            json,
          );
        } else {
          setBranchName(json.branch[0].branch_name);
          setBranchAddress(json.branch[0].address);
          setBranchSuburb(json.branch[0].suburb);
          setBranchPostcode(json.branch[0].postcode);
          setBranchEmail(json.branch[0].email);
          setBranchPhone(json.branch[0].phone);
          setBranchLogo(
            json.branch[0].header_image
              ? json.branch[0].header_image
              : "/ZedmedLogoStacked.png",
          );
          setDatabaseId(json.branch[0].database_id);
        }
      })
      .catch((err) => {
        console.log(
          "[ERROR] An error occured while loading practice data during initialization",
          err,
        );
      });

    fetch("/api/v1/relatedForms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(postData),
    })
      .then((response) => response.json())
      .then((json) => {
        if (json.error) {
          setForms([]);
          setPracticeMessage("There are no forms for you to complete.");
        } else {
          if (json.forms && json.forms.length) {
            setForms(json.forms);
            setPracticeMessage("");

            if (isPageRefresh()) {
              const persistedFormId = readStorage(openFormStorageKey);
              const persistedForm =
                persistedFormId &&
                json.forms.find((f) => String(f.form_id) === persistedFormId);
              if (persistedForm) {
                loadForm(persistedForm);
              }
            } else {
              // A typed/clicked navigation always starts at the list - clear
              // any stale "form was open" state so a later refresh, taken
              // while still on the list, doesn't unexpectedly jump into it.
              writeStorage(openFormStorageKey, null);
            }
          } else {
            setForms([]);
            setPracticeMessage("There are no forms for you to complete.");
          }
        }
      })
      .catch((err) => {
        console.log(
          "[ERROR] An error occured while loading form data during initialization",
          err,
        );
        setForms([]);
        setPracticeMessage("An error occured while loading forms to complete.");
      });
  };

  useEffect(() => {
    loadPatientForms();
  }, []);

  const loadForm = (form) => {
    const selectedForm = JSON.parse(form.template);

    setShowForm(true);
    setPracticeMessage(selectedForm.FormName);
    setQuestions(selectedForm.Questions.map((q) => q));
    setSelectedForm(form.form_id);
    writeStorage(openFormStorageKey, form.form_id);
  };

  // Runs after the newly-selected form's controls have mounted, so it can
  // write previously-entered values back into them - restoring an
  // in-progress form after a refresh, not just which form was open.
  useLayoutEffect(() => {
    if (!formRef.current || !selectedForm) return;

    const saved = readStorage(answersStorageKey(selectedForm));
    if (!saved) return;

    try {
      restoreFormValues(formRef.current, JSON.parse(saved));
    } catch {
      // Corrupt/incompatible saved data - safe to just skip restoring.
    }
  }, [questions, selectedForm]);

  const submitForm = async (formData) => {
    let completedQuestions = questions.map((q) =>
      collectQuestionValue(q, formData),
    );

    let completedFormHtml = document.getElementById("form-container");

    const options = {
      margin: 10,
      filename: "completed_form.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
    };

    html2pdf()
      .set(options)
      .from(completedFormHtml)
      .outputPdf("blob")
      .then((pdfBlob) => {
        const formData = new FormData();
        formData.append("formId", selectedForm);
        formData.append("databaseId", databaseId);
        formData.append(
          "completedQuestions",
          JSON.stringify(completedQuestions),
        );
        formData.append("file", pdfBlob, "form.pdf");

        fetch("/api/v1/completeform", {
          method: "POST",
          body: formData,
        })
          .then((response) => response.json())
          .then((json) => {
            setShowForm(false);
            writeStorage(openFormStorageKey, null);
            writeStorage(answersStorageKey(selectedForm), null);
            loadPatientForms();
          })
          .catch((err) => {
            console.log(
              "[ERROR] An error occured while notification to Zedmed API ",
              err,
            );
          });
      });
  };

  const blockEnterSubmit = (event) => {
    if (
      event.key === "Enter" &&
      event.target.tagName !== "TEXTAREA" &&
      event.target.type !== "submit"
    ) {
      event.preventDefault();
    }
  };

  // Delegated (bubbles via focusout) so every control - including ones in
  // conditionally-rendered sub-questions - is validated as soon as it's
  // left, without wiring an onBlur into every single builder above.
  const handleControlBlur = (event) => {
    const isSelfManaged =
      event.target.hasAttribute("data-date-part") ||
      event.target.hasAttribute("data-group-validated");
    if (!isSelfManaged) {
      // DateQuestion and MultiChoiceCheckQuestion's group-count validation
      // already manage their own per-field/per-group validity styling on
      // every change - applyValidityClass's "leave blank fields alone"
      // shortcut would otherwise clear an unchecked-but-invalid group
      // member's red styling the moment it's blurred past.
      applyValidityClass(event.target);
    }
    persistFormValues();
  };

  // Covers every keystroke/selection change (radio, checkbox, dropdown,
  // typing) so what survives a refresh is never more than one interaction
  // stale, and keeps each question's inline error text in sync with every
  // field-specific onChange validator (Medicare/date parts/checkbox group
  // counts/etc.) the moment it toggles is-invalid, not just on blur.
  const handleControlChange = (event) => {
    updateGroupFeedback(event.target);
    persistFormValues();
  };

  // Gates submission from the submit button's onClick rather than the
  // form's onSubmit: with React 19's action={submitForm}, preventDefault()
  // from a sibling onSubmit handler does not reliably stop the action from
  // running, which previously let invalid data through. Cancelling the
  // button's own click - its default action is "submit the form" - blocks
  // it before any submit/action machinery gets involved. The button stays
  // enabled/clickable; this is what actually prevents an invalid submit.
  const handleSubmitClick = (event) => {
    const form = formRef.current;
    if (!form) return;

    // Recompute every date group's hidden combined value straight from its
    // current day/month/year/time boxes, regardless of what put those values
    // there. Typing/blurring already keeps this in sync via its own
    // handlers, but browser autofill can set several of a group's fields at
    // once without dispatching an input/change event for each one - leaving
    // the hidden field stuck at whatever (often empty) it last computed to.
    // This guarantees it reflects reality at the one moment it actually
    // matters, independent of which events did or didn't fire along the way.
    form
      .querySelectorAll(".date-input-group")
      .forEach((group) => syncDateValue(group));

    let firstInvalid = null;

    form.querySelectorAll("input, select, textarea").forEach((control) => {
      // A filled-in date part has already been styled by its own handler on
      // every keystroke; only a still-blank one needs flagging here.
      const skipStyling =
        control.hasAttribute("data-date-part") && control.value;
      if (!skipStyling) {
        applyValidityClass(control, { onSubmit: true });
      }

      if (!firstInvalid && !control.checkValidity()) {
        firstInvalid = control;
      }
    });

    if (firstInvalid) {
      event.preventDefault();
      firstInvalid.focus();
    }
  };

  // Returns to the forms list, discarding whatever has been typed into the
  // current form - clears its persisted "open form"/answers state too, so a
  // later refresh (or reselecting the same form) doesn't resurrect it.
  const handleBackClick = () => {
    writeStorage(openFormStorageKey, null);
    if (selectedForm) {
      writeStorage(answersStorageKey(selectedForm), null);
    }
    setShowForm(false);
  };

  return (
    <Container className="mt-2 mb-2" ref={completedForm} id="form-container">
      <Row>
        <Col></Col>
        <Col xs={10}>
          <Card className="w-100 mb-5" border="secondary-light">
            <Row>
              <Col xs={8}>
                <Card.Body>
                  <Card.Title>{branchName}</Card.Title>
                  <div>
                    <Card.Text>{branchAddress}</Card.Text>
                    <Card.Text>
                      {[branchSuburb, branchPostcode]
                        .filter(Boolean)
                        .join(", ")}
                    </Card.Text>
                    <Card.Text>{branchEmail}</Card.Text>
                    <Card.Text>{branchPhone}</Card.Text>
                  </div>
                </Card.Body>
              </Col>
              <Col>
                <Card.Img src={branchLogo} />
              </Col>
            </Row>
            <CardFooter className="footer text-danger">
              {showForm ? "* Indicates required question" : practiceMessage}
            </CardFooter>
          </Card>
          {!showForm && (
            <ListGroup className="mt-2 mb-2">
              {forms &&
                forms.map((form, index) => (
                  <ListGroup.Item
                    action
                    className="mt-1 mb-1"
                    style={{ borderTopWidth: 1 }}
                    key={index}
                    onClick={() => loadForm(form)}
                  >
                    {form.form_description || "Please complete the form"}
                  </ListGroup.Item>
                ))}
            </ListGroup>
          )}
          {showForm && questions && questions.length && (
            <Form
              ref={formRef}
              action={submitForm}
              onBlur={handleControlBlur}
              onChange={handleControlChange}
              onKeyDown={blockEnterSubmit}
              method="POST"
              noValidate
            >
              {questions.map((question, index) => {
                if (!hasRenderableControl(question)) return null;
                return (
                  <Card className="mb-3" key={index}>
                    <Card.Body>
                      <Card.Title as="div">
                        <div className="d-flex align-items-start">
                          {question.IsMandatory && (
                            <span className="text-danger me-1">*</span>
                          )}

                          <SanitizedHtml
                            html={question.MainText}
                            className="question-main-text"
                          />
                        </div>
                      </Card.Title>

                      <SanitizedHtml
                        html={question.Description}
                        className="card-text question-description"
                      />
                      <Form.Group
                        className="mb-3"
                        data-error-text={question.ErrorText || ""}
                      >
                        {buildControl(question)}
                      </Form.Group>
                    </Card.Body>
                  </Card>
                );
              })}
              <div className="d-flex justify-content-between">
                <Button
                  variant="primary"
                  type="submit"
                  onClick={handleSubmitClick}
                >
                  Submit
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  onClick={handleBackClick}
                >
                  Back
                </Button>
              </div>
            </Form>
          )}
        </Col>
        <Col></Col>
      </Row>
      <Row>
        <Col className="text-center text-muted small mt-3">
          Powered by Zedmed v1.0.1
        </Col>
      </Row>
    </Container>
  );
}

export default App;
